import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { parseISO } from 'date-fns';
import { Search, Flame, CalendarDays, MapPin, ChevronDown, SlidersHorizontal, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import API from '../config/api';
import PujaFilterModal from '../components/puja/PujaFilterModal';

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const d = parseISO(dateStr.slice(0, 10));
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

const getPujaDate = puja => (puja.is_weekly && puja.calculated_puja_date)
  ? puja.calculated_puja_date
  : puja.puja_date;

const DEFAULT_DEITIES = [
  'All',
  'Brihaspati',
  'Budh',
  'Ganga Maiya',
  'Gau Mata',
  'Kaal Bhairavji',
  'Khatu Shyam',
  'Lalita Tripura Sundari',
  'Ma Baglamukhi',
  'Ma Durga',
  'Mahadev',
  'Ma Kali',
  'Ma Kamakhya',
  'Mata Laxmi',
  'Matangi',
  'Mata Saraswati',
  'Nag Dev',
  'Pitru Dev',
  'Rahu',
  'Shani Dev',
  'Shiv',
  'Shri Ganesh',
  'Shri Hanuman Ji',
  'Shri Krishna',
  'Shri Vishnu',
  'Surya Dev',
  'Varahi Mata'
];

const DEFAULT_TEMPLE_TYPES = [
  'All',
  'Ashtvinayak',
  'Ayodhya',
  'General',
  'Haridwar',
  'Jyotirlinga',
  'Kangra',
  'Kashi',
  'Panch Kedar',
  'Prayagraj',
  'Sapta Moksha Puri',
  'Shaktipeeth',
  'Tarapith'
];

const PujasPage = () => {
  const [pujas, setPujas] = useState([]);
  const [temples, setTemples] = useState([]);
  const [deities, setDeities] = useState(DEFAULT_DEITIES);
  const [templeTypes, setTempleTypes] = useState(DEFAULT_TEMPLE_TYPES);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');

  // Modal Filters (Item 12 matching docs/img/details-page-3.png and details-page-4.png)
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [modalFilters, setModalFilters] = useState({
    pujaTypes: [],
    deities: [],
    benefits: [],
    templeTypes: [],
    weeklyDays: [],
    tithis: [],
    startDate: '',
    endDate: ''
  });

  // Pagination / Infinite Scroll (Item 13: Show 20 puja at a time and load more on scroll)
  const [visibleCount, setVisibleCount] = useState(20);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pujasRes, templesRes, deitiesRes, templeTypesRes] = await Promise.allSettled([
          axios.get(`${API}/pujas`),
          axios.get(`${API}/temples`),
          axios.get(`${API}/deities`),
          axios.get(`${API}/temple-types`)
        ]);

        if (pujasRes.status === 'fulfilled' && Array.isArray(pujasRes.value.data)) {
          setPujas(pujasRes.value.data);
        }
        if (templesRes.status === 'fulfilled' && Array.isArray(templesRes.value.data)) {
          setTemples(templesRes.value.data);
        }
        if (deitiesRes.status === 'fulfilled' && Array.isArray(deitiesRes.value.data) && deitiesRes.value.data.length > 0) {
          const names = deitiesRes.value.data.map(d => d.name).filter(Boolean);
          setDeities(['All', ...Array.from(new Set(names))]);
        }
        if (templeTypesRes.status === 'fulfilled' && Array.isArray(templeTypesRes.value.data) && templeTypesRes.value.data.length > 0) {
          const names = templeTypesRes.value.data.map(t => t.name).filter(Boolean);
          setTempleTypes(['All', ...Array.from(new Set(names))]);
        }
      } catch (error) {
        console.error('Error fetching pujas page data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getTemple = (templeId) => temples.find(t => t.id === templeId);

  // Count active filters
  const totalActiveFilters =
    (modalFilters.pujaTypes?.length || 0) +
    (modalFilters.deities?.length || 0) +
    (modalFilters.benefits?.length || 0) +
    (modalFilters.templeTypes?.length || 0) +
    (modalFilters.weeklyDays?.length || 0) +
    (modalFilters.tithis?.length || 0) +
    (modalFilters.startDate || modalFilters.endDate ? 1 : 0);

  // Filter pujas based on search and the hero filter dialog
  const filteredPujas = pujas.filter(puja => {
    const temple = getTemple(puja.temple_id);

    // 1. Text Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        puja.name?.toLowerCase().includes(q) ||
        temple?.name?.toLowerCase().includes(q) ||
        puja.deity?.toLowerCase().includes(q) ||
        puja.temple_type?.toLowerCase().includes(q) ||
        puja.description?.toLowerCase().includes(q) ||
        puja.about_puja?.toLowerCase().includes(q) ||
        (puja.benefits && Array.isArray(puja.benefits) && puja.benefits.some(b => b.toLowerCase().includes(q)));
      if (!matchesSearch) return false;
    }

    // 2. Deity Filter
    if (modalFilters.deities?.length > 0) {
      const matches = modalFilters.deities.some(d =>
        puja.deity?.toLowerCase() === d.toLowerCase() ||
        puja.name?.toLowerCase().includes(d.toLowerCase())
      );
      if (!matches) return false;
    }

    // 3. Temple Type Filter
    if (modalFilters.templeTypes?.length > 0) {
      const matches = modalFilters.templeTypes.some(t =>
        puja.temple_type?.toLowerCase() === t.toLowerCase() ||
        temple?.temple_type?.toLowerCase() === t.toLowerCase() ||
        temple?.name?.toLowerCase().includes(t.toLowerCase())
      );
      if (!matches) return false;
    }

    // 4. Puja Type Filter
    if (modalFilters.pujaTypes?.length > 0) {
      const matches = modalFilters.pujaTypes.some(pt => {
        if (pt === 'Weekly Ritual') return !!puja.is_weekly;
        if (pt === 'One-Time Ritual') return !puja.is_weekly;
        const ptLower = pt.toLowerCase();
        return (
          puja.name?.toLowerCase().includes(ptLower) ||
          puja.description?.toLowerCase().includes(ptLower) ||
          puja.about_puja?.toLowerCase().includes(ptLower)
        );
      });
      if (!matches) return false;
    }

    // 5. Benefit Filter
    if (modalFilters.benefits?.length > 0) {
      const matches = modalFilters.benefits.some(b => {
        const bLower = b.toLowerCase();
        return (
          (puja.benefits && Array.isArray(puja.benefits) && puja.benefits.some(pb => pb.toLowerCase().includes(bLower))) ||
          puja.description?.toLowerCase().includes(bLower) ||
          puja.why_perform_en?.toLowerCase().includes(bLower) ||
          puja.why_perform?.toLowerCase().includes(bLower)
        );
      });
      if (!matches) return false;
    }

    // 6. Weekly Day Filter
    if (modalFilters.weeklyDays?.length > 0) {
      const matches = modalFilters.weeklyDays.some(wDay => {
        const cleanDay = wDay.split(' ')[0].toLowerCase();
        return (
          puja.weekly_day?.toLowerCase().includes(cleanDay) ||
          puja.name?.toLowerCase().includes(cleanDay) ||
          puja.description?.toLowerCase().includes(cleanDay)
        );
      });
      if (!matches) return false;
    }

    // 7. Upcoming Tithi Filter
    if (modalFilters.tithis?.length > 0) {
      const matches = modalFilters.tithis.some(t => {
        const tLower = t.toLowerCase();
        return (
          puja.name?.toLowerCase().includes(tLower) ||
          puja.description?.toLowerCase().includes(tLower) ||
          puja.upcoming_tithi?.toLowerCase().includes(tLower) ||
          puja.about_puja?.toLowerCase().includes(tLower)
        );
      });
      if (!matches) return false;
    }

    // Compare date-only values so the entire To Date is included.
    const pujaDateStr = getPujaDate(puja)?.slice(0, 10);
    if (modalFilters.startDate || modalFilters.endDate) {
      if (!pujaDateStr || !/^\d{4}-\d{2}-\d{2}$/.test(pujaDateStr)) return false;
      if (modalFilters.startDate && pujaDateStr < modalFilters.startDate) return false;
      if (modalFilters.endDate && pujaDateStr > modalFilters.endDate) return false;
    }

    return true;
  });

  // Reset pagination to 20 whenever search or filters change
  useEffect(() => {
    setVisibleCount(20);
  }, [searchQuery, modalFilters]);

  // Infinite scroll listener (Item 13)
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 350) {
        setVisibleCount(prev => {
          if (prev < filteredPujas.length) {
            return Math.min(prev + 20, filteredPujas.length);
          }
          return prev;
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [filteredPujas.length]);

  const resetAllFilters = () => {
    setSearchQuery('');
    setModalFilters({
      pujaTypes: [],
      deities: [],
      benefits: [],
      templeTypes: [],
      weeklyDays: [],
      tithis: [],
      startDate: '',
      endDate: ''
    });
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-[#FBF4E7]">

        {/* ═══ SACRED HERO (ref: docs/puja.md) ═══ */}
        <div className="relative py-12 md:py-16 bg-gradient-to-b from-[#6E1B34] via-[#4E1225] to-[#2A1F16] text-white overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(#D4AF37_1px,transparent_1px)] [background-size:20px_20px] opacity-10 pointer-events-none" />

          <div className="relative max-w-5xl mx-auto px-4 text-center z-10">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4AF37]/40 bg-white/10 backdrop-blur-md mb-4">
                <span className="w-2 h-2 rounded-full bg-[#FF7B00] animate-pulse" />
                <span className="text-[#E8D5A8] font-bold text-xs uppercase tracking-widest">
                  Authentic Temple Pujas Online
                </span>
              </div>

              {/* Exact Header Title from docs/puja.md */}
              <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black text-white mb-3 drop-shadow-md">
                Book Sacred Pujas at <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF9933] to-[#FFD700]">India's Holiest Temples</span>
              </h1>

              {/* Exact Subtitle from docs/puja.md */}
              <p className="text-[#FBF4E7]/90 text-sm sm:text-base max-w-3xl mx-auto mb-8 font-normal leading-relaxed">
                Choose from 650+ authentic Vedic rituals performed by qualified pandits at renowned temples across India.
              </p>

              {/* Search Bar & Filter Button (docs/img/details-page-3.png) */}
              <div className="max-w-xl mx-auto flex items-center gap-3">
                <div className="flex-1 relative flex items-center bg-white rounded-2xl pl-4 pr-2 py-1.5 shadow-xl border border-gray-200">
                  <Search className="w-5 h-5 text-gray-400 mr-2 flex-shrink-0" />
                  <Input
                    placeholder="Search for Mangal Dosh Nivaran..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 bg-transparent border-0 text-[#2A1F16] placeholder-gray-400 focus:ring-0 text-sm sm:text-base px-0 h-11"
                    data-testid="pujas-search-input"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="text-xs text-gray-400 hover:text-gray-600 px-2 font-bold"
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* Filter Icon Button (details-page-3.png) */}
                <button
                  type="button"
                  onClick={() => setIsFilterModalOpen(true)}
                  className="h-12 w-12 rounded-2xl bg-white border border-gray-200 shadow-xl flex items-center justify-center text-[#EA8A2A] hover:bg-orange-50/50 hover:border-[#EA8A2A] transition-all flex-shrink-0 relative cursor-pointer"
                  data-testid="open-filter-modal-btn"
                  title="Open Filters"
                >
                  <SlidersHorizontal className="w-5 h-5" />
                  {totalActiveFilters > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 bg-[#EA8A2A] text-white text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center shadow-md animate-scale">
                      {totalActiveFilters}
                    </span>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-[#E7D9BC]">
            <p className="text-xs text-[#83725B]">
              Showing <strong className="text-[#2A1F16]">{Math.min(visibleCount, filteredPujas.length)}</strong> of <strong className="text-[#2A1F16]">{filteredPujas.length}</strong> sacred {filteredPujas.length === 1 ? 'puja' : 'pujas'}
            </p>
            {(searchQuery || totalActiveFilters > 0) && (
              <button onClick={resetAllFilters} className="text-xs font-bold text-[#6E1B34] hover:underline cursor-pointer">
                Clear All Filters
              </button>
            )}
          </div>

          {/* Active Filter Chips (if any from modal) */}
          {totalActiveFilters > 0 && (
            <div className="flex flex-wrap items-center gap-2 pt-3 pb-1">
              <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Active Filters:</span>
              {modalFilters.deities?.map(d => (
                <span key={d} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-900 border border-orange-200">
                  <span>Deity: {d}</span>
                  <button onClick={() => setModalFilters(prev => ({ ...prev, deities: prev.deities.filter(x => x !== d) }))} className="hover:text-black">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {modalFilters.templeTypes?.map(t => (
                <span key={t} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-900 border border-amber-200">
                  <span>Temple: {t}</span>
                  <button onClick={() => setModalFilters(prev => ({ ...prev, templeTypes: prev.templeTypes.filter(x => x !== t) }))} className="hover:text-black">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {modalFilters.pujaTypes?.map(pt => (
                <span key={pt} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-900 border border-red-200">
                  <span>Type: {pt}</span>
                  <button onClick={() => setModalFilters(prev => ({ ...prev, pujaTypes: prev.pujaTypes.filter(x => x !== pt) }))} className="hover:text-black">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {modalFilters.benefits?.map(b => (
                <span key={b} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-900 border border-emerald-200">
                  <span>Benefit: {b}</span>
                  <button onClick={() => setModalFilters(prev => ({ ...prev, benefits: prev.benefits.filter(x => x !== b) }))} className="hover:text-black">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {modalFilters.weeklyDays?.map(w => (
                <span key={w} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-900 border border-blue-200">
                  <span>Day: {w}</span>
                  <button onClick={() => setModalFilters(prev => ({ ...prev, weeklyDays: prev.weeklyDays.filter(x => x !== w) }))} className="hover:text-black">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {modalFilters.tithis?.map(t => (
                <span key={t} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-900 border border-purple-200">
                  <span>Tithi: {t}</span>
                  <button onClick={() => setModalFilters(prev => ({ ...prev, tithis: prev.tithis.filter(x => x !== t) }))} className="hover:text-black">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {(modalFilters.startDate || modalFilters.endDate) && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-900 border border-gray-200">
                  <span>Date: {modalFilters.startDate || 'Any'} to {modalFilters.endDate || 'Any'}</span>
                  <button onClick={() => setModalFilters(prev => ({ ...prev, startDate: '', endDate: '' }))} className="hover:text-black">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          )}

          {/* ═══ PUJAS GRID (ref: docs/puja.md) ═══ */}
          <div className="py-6">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                {Array(6).fill(0).map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl h-[440px] animate-pulse border border-[#E7D9BC]/70" />
                ))}
              </div>
            ) : filteredPujas.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-[#E7D9BC] p-8">
                <Flame className="w-12 h-12 text-[#FF7B00] mx-auto mb-3" />
                <h3 className="font-heading text-lg font-bold text-gray-700 mb-1">No Pujas Found</h3>
                <p className="text-xs text-gray-500 mb-4">Try clearing your filters or search keywords to explore all sacred ceremonies.</p>
                <button
                  onClick={resetAllFilters}
                  className="px-4 py-2 bg-[#6E1B34] text-white text-xs font-bold rounded-xl cursor-pointer"
                >
                  Reset All Filters
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                  {filteredPujas.slice(0, visibleCount).map((puja, index) => {
                    const temple = getTemple(puja.temple_id);
                    const pujaUrl = `/puja/${puja.slug || puja.id}`;
                    const pujaDate = getPujaDate(puja);

                    return (
                      <motion.div
                        key={puja.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: (index % 6) * 0.05 }}
                        className="group relative min-w-0 bg-white rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(78,18,37,0.06)] border border-[#E7D9BC]/70 hover:border-[#D9711F]/60 transition-all duration-300 flex flex-col justify-between"
                        data-testid={`puja-card-${puja.id}`}
                      >
                        <Link to={pujaUrl} aria-label={`View ${puja.name}`}
                          className="block shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#D9711F]">
                          <div className="relative h-52 sm:h-52 md:h-56 overflow-hidden bg-[#F3E7D0]/40">
                            <img
                              src={puja.image_url || temple?.image_url || 'https://eastindiantraveller.com/wp-content/uploads/2020/10/fb_img_1602601852796-1.jpg'}
                              alt={puja.name}
                              loading="lazy"
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                            />
                          </div>
                        </Link>
                        <div className="p-4 sm:p-5 flex-1 flex flex-col">
                          <dl className="mb-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs font-medium text-[#83725B]">
                            <div className="min-w-0">
                              <dt className="sr-only">Deity</dt>
                              <dd className="break-words">{puja.deity || temple?.deity || 'Not specified'}</dd>
                            </div>
                            <div className="min-w-0 text-[#D9711F]">
                              <dt className="sr-only">Temple Type</dt>
                              <dd className="break-words">{puja.temple_type || temple?.temple_type || 'Not specified'}</dd>
                            </div>
                          </dl>

                          <Link to={pujaUrl} className="mb-1.5 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D9711F]">
                            <h3 title={puja.name} className="font-heading text-sm sm:text-base font-bold text-[#4E1225] group-hover:text-[#D9711F] transition-colors line-clamp-2">
                              {puja.name}
                            </h3>
                          </Link>

                          <dl className="mb-4 text-xs leading-relaxed text-[#83725B]">
                            <div className="flex min-w-0 items-start gap-2">
                              <MapPin aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#D9711F]" />
                              <dt className="sr-only">Temple</dt>
                              <dd className="line-clamp-2" title={temple?.name}>{temple?.name || 'To be announced'}</dd>
                            </div>
                          </dl>

                          <div className="mt-auto flex items-center justify-between gap-2 border-t border-[#E7D9BC]/50 pt-3">
                            <dl className="min-w-0 text-[11px] sm:text-xs font-medium leading-relaxed text-[#4E1225]">
                              <div className="flex items-start gap-1.5">
                                <CalendarDays aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#D9711F]" />
                                <dt className="sr-only">Puja Date</dt>
                                <dd>{formatDate(pujaDate) || 'To be announced'}</dd>
                              </div>
                            </dl>
                            <Link to={pujaUrl} aria-label={`Book Puja: ${puja.name}`}
                              className="bg-[#6E1B34] hover:bg-[#4E1225] text-white rounded-xl px-3.5 py-2 text-xs font-semibold shadow-xs inline-flex min-h-10 items-center justify-center whitespace-nowrap hover:scale-105 active:scale-95 transition-all shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6E1B34] focus-visible:ring-offset-2"
                              data-testid={`book-puja-${puja.id}`}>
                              Book Puja
                            </Link>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Item 13: Show 20 puja at a time & load more on scroll / click */}
                {filteredPujas.length > visibleCount && (
                  <div className="mt-12 text-center flex flex-col items-center justify-center gap-3">
                    <p className="text-xs text-gray-500 font-medium">
                      Showing <strong className="text-[#6E1B34]">{visibleCount}</strong> of <strong className="text-[#2A1F16]">{filteredPujas.length}</strong> sacred pujas
                    </p>
                    <Button
                      onClick={() => setVisibleCount(prev => Math.min(prev + 20, filteredPujas.length))}
                      className="btn-saffron py-2.5 px-8 rounded-xl font-bold text-xs shadow-md flex items-center gap-1.5 cursor-pointer"
                      data-testid="load-more-pujas-btn"
                    >
                      <span>Load More Pujas ({filteredPujas.length - visibleCount} Remaining)</span>
                      <ChevronDown className="w-4 h-4 ml-1" />
                    </Button>
                    <span className="text-[11px] text-gray-400">Scroll down to automatically load more</span>
                  </div>
                )}

                {filteredPujas.length > 20 && visibleCount >= filteredPujas.length && (
                  <div className="mt-10 text-center text-xs text-gray-400 font-medium py-3 border-t border-gray-200/60 max-w-md mx-auto">
                    ✓ All {filteredPujas.length} sacred temple ceremonies loaded
                  </div>
                )}
              </>
            )}
          </div>

        </div>
      </div>

      {/* Item 12: Filter Modal matching docs/img/details-page-4.png */}
      <PujaFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={modalFilters}
        onApply={(newFilters) => setModalFilters(newFilters)}
        onClear={() => setModalFilters({
          pujaTypes: [],
          deities: [],
          benefits: [],
          templeTypes: [],
          weeklyDays: [],
          tithis: [],
          startDate: '',
          endDate: ''
        })}
        availableDeities={deities}
        availableTempleTypes={templeTypes}
      />

      <Footer />
    </>
  );
};

export default PujasPage;
